/**
 * Date utility functions for timezone-safe date handling.
 * Uses local timezone methods explicitly to avoid timezone shifts.
 */

/**
 * Format a Date object to YYYY-MM-DD string using local timezone methods.
 * This ensures the formatted date matches what the user selected and avoids
 * timezone conversion issues.
 * 
 * Similar to formatDateOfBirth() in the birthday implementation.
 * 
 * @param date - Date object to format
 * @returns YYYY-MM-DD string, or empty string if date is invalid
 */
export const formatDateToYYYYMMDD = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  
  // Use local timezone methods for consistency
  // This ensures the formatted date matches what the user selected
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date (Date object or YYYY-MM-DD string) to a Date object at local midnight.
 * Dates are just days, not times - no timezone conversion needed.
 * 
 * This function handles:
 * - Date objects: Normalizes to local midnight
 * - YYYY-MM-DD strings: Parses in local timezone
 * - ISO strings (with 'T'): Parses and normalizes to local midnight
 * - null/undefined: Returns null
 * 
 * @param date - Date object, YYYY-MM-DD string, ISO string, or null/undefined
 * @returns Date object at local midnight, or null if invalid
 */
export const parseLocalDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  
  if (date instanceof Date) {
    // Already a Date - check if valid first
    if (isNaN(date.getTime())) return null;
    // Normalize to local midnight
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
  }
  
  if (typeof date === 'string') {
    // Parse YYYY-MM-DD string in local timezone (not UTC)
    // If it contains 'T', it might be ISO format - handle that differently
    if (date.includes('T')) {
      const parsed = new Date(date);
      if (isNaN(parsed.getTime())) return null;
      // Normalize to local midnight
      return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
      );
    }
    // Parse YYYY-MM-DD format in local timezone
    const [year, month, day] = date.split('-').map(Number);
    const parsed = new Date(year, month - 1, day); // month is 0-indexed
    if (isNaN(parsed.getTime())) return null;
    return parsed;
  }
  
  return null;
};

/**
 * Default check-in/check-out for Nuitee augmentation search (e.g. next Friday–Sunday).
 * Used when no user-selected dates; one shared helper for Results Nuitee call and BookingModal from Nuitee cards.
 */
export function getDefaultNuiteeDates(): { checkIn: string; checkOut: string } {
  const now = new Date();
  const day = now.getDay(); // 0 Sun .. 6 Sat; 5 = Friday
  const daysUntilFriday = day === 5 ? 7 : (5 - day + 7) % 7 || 7; // next Friday (never today)
  const checkInDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilFriday);
  const checkOutDate = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate() + 2);
  return {
    checkIn: formatDateToYYYYMMDD(checkInDate),
    checkOut: formatDateToYYYYMMDD(checkOutDate),
  };
}

